import useDetectOutsideClick from "@hooks/useDetectOutsideClick";
import ModalManager from "@utils/ModalManager";
import { useLang } from "@hooks/useLang";
import moment from "moment";
import { Input } from "@components/common/input";
import { useInputHook } from "@hooks/useInputHook";
import _ from "lodash";
import { FORMAT, MAX_LENGTH_ANALYTICS_NAME } from "@resources/Constants";
import { useState, useRef } from "react";
import { HVButton, HVIcon } from "@repo/HVComponents";
import { ICON } from "src/constants/icons";
import * as S from "../LeftMenu.styled";

type SAVE_TYPE = "SAVE" | "SAVE_AS";
const MAX_SAVE_FILTER = 10;
const DEBOUNCE_TIME_MS = 100;

interface SaveButtonProps {
  onSave: (
    status: SAVE_TYPE,
    options: {
      fileName?: string;
    }
  ) => void;
  disabled?: boolean;
  savedCount: number;
}

export const SaveButton = (props: SaveButtonProps) => {
  const { translate } = useLang();
  const { disabled = false } = props;
  const [isOpen, setIsOpen] = useState(false);
  const moreRef = useRef(null);
  useDetectOutsideClick(moreRef, () => setIsOpen(false));
  const { setValue, value, inputRef, getValueFromRef } = useInputHook<
    string | undefined
  >();

  const handleSave = _.debounce((type: SAVE_TYPE, fileName?: string) => {
    props.onSave(type, { fileName });
  }, DEBOUNCE_TIME_MS);

  const handleClickSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    const type =
      ((e.target as HTMLElement).dataset.id as SAVE_TYPE) || ("SAVE" as const);
    setIsOpen(false);

    // max count
    if (type === "SAVE_AS") {
      if (MAX_SAVE_FILTER <= props.savedCount) {
        ModalManager.message(
          translate("max_filter_count").replace("{max}", `${MAX_SAVE_FILTER}`),
          {
            title: translate("system"),
            labelButton: translate("ok")
          }
        );
        return;
      }
    }

    if (type === "SAVE_AS" || props.savedCount === 0) {
      const defaultName = moment().format(FORMAT.DATE_TIME_24H_1);
      // 1) input name
      ModalManager.confirm(
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <Input
            innerRef={inputRef}
            defaultValue={defaultName}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            maxLength={MAX_LENGTH_ANALYTICS_NAME}
            style={{
              width: "352px"
            }}
          />
          <HVButton
            variant="Ghost"
            buttonContainerProps={{
              w: 20,
              h: 20,
              p: 0,
              styles: {
                root: {
                  position: "absolute",
                  right: 8,
                  display: "flex"
                }
              }
            }}
            onClick={(e) => {
              if (inputRef.current) {
                inputRef.current.value = "";
              }
              e.stopPropagation();
              setValue("");
              inputRef.current?.focus();
            }}
          >
            <HVIcon
              name="fail"
              scale="large"
              theme="fill"
              svgProps={{
                ...ICON.SIZE_20,
                stroke: "var(--hv-text-on-brand-tertiary) !important"
              }}
            />
          </HVButton>
        </div>,
        {
          title: translate(type.toLocaleLowerCase()),
          labelConfirm: translate("save"),
          labelCancel: translate("cancel"),
          onSubmit: () => {
            const fileName = getValueFromRef() ?? defaultName;
            handleSave(type, fileName);
            _.defer(() => setValue(undefined));
          },
          setShowModal: (visible: boolean) => {
            if (!visible) {
              _.defer(() => setValue(undefined));
            }
          }
        }
      );
    } else {
      handleSave(type);
    }
  };

  return (
    <S.SaveButton ref={moreRef} disabled={disabled} onClick={handleClickSave}>
      <span>{translate("save")}</span>
      <button
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(true);
        }}
      >
        <HVIcon
          name="arrow-chevron-down"
          svgProps={{
            ...ICON.SIZE_16,
            stroke: `var(--hv-icon-on-brand-${
              disabled ? "disabled" : "primary"
            })`
          }}
        />
      </button>
      {isOpen && (
        <S.DropDownList>
          <li data-id={"SAVE" as const}>{translate("save")}</li>
          <li data-id={"SAVE_AS" as const}>{translate("save_as")}</li>
        </S.DropDownList>
      )}
    </S.SaveButton>
  );
};
